import Chance from 'chance';
import { isEmpty } from 'lodash';
import { Fragment } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { formatDuration, getRoundPrice } from '../../../helpers/results';
import {
  BUS,
  CARPOOLING,
  COMPANY_VERBS,
  COMPANY_VERBS_PLURAL,
  MULTIMODAL,
  SPECIFIC_TEXT_DAILY_TRIPS,
  SPECIFIC_TEXT_HEADERS,
  SPECIFIC_TEXT_NO_DATA_INTRO,
  SPECIFIC_TEXT_NO_DATA_ITINERARIES,
  SPECIFIC_TEXT_NO_DATA_ITINERARY_DISTANCE,
  SPECIFIC_TEXT_NO_DATA_ITINERARY_STOPS,
  SPECIFIC_TEXTS,
  SPECIFIC_TEXTS_TAIL,
  TRAIN,
  TRANSPORT_MEANS_EMOJIS,
  TRANSPORT_MEANS_PLURAL,
} from '../constants';
import { SearchPageType } from '../types';
import messages from '../View/messages';
import { SpecificTextMeanHeader, SubContent } from '../View/TextGen';
import { TextGen } from './TextGen';

/**
 * @description TextGen class that formats the specific text to describe the trip (second accordion).
 * Specific text contains information such as prices, durations, number of daily trips, for each
 * transport mean when historyData is present (this search has already been made on TTT),
 * or more generic information such as possible trips with stops and distances if there is no history data.
 * It is also largely used to induce variations between search pages.
 */
class SpecificTextGen extends TextGen {
  constructor(textGen) {
    super(textGen);
    this.intl = textGen.intl;
    this.chance = textGen.chance;
    this.seed = textGen.seed;
  }

  /**
   * @description Main function formatting the general text block with one header and a content body.
   * @param {object} state - State data
   * @returns {{header: FormattedMessage, content: Fragment[]}|null}
   */
  formatSpecificTextData(state) {
    if (
      isEmpty(state.tripData) ||
      state.currentPageType !== SearchPageType.GENERIC
    ) {
      return null;
    }
    const means = [BUS, TRAIN, MULTIMODAL, CARPOOLING];
    const content = [];

    means.forEach((mean, i) => {
      if (mean === CARPOOLING) {
        content.push(this.getCarpoolingContent(state, mean, i));
      } else if (state.tripData.transportInformation.historyData) {
        content.push(this.getHistoryContent(state, mean, i));
      } else {
        content.push(this.getNoHistoryContent(state, mean, i));
      }
    });

    return {
      header: (
        <FormattedMessage
          {...messages.specificTextHeader}
          values={{
            origin: state.originCity.name,
            destination: state.destinationCity.name,
          }}
        />
      ),
      content,
    };
  }

  /**
   * @description Formats a block of text for one transport mean WITH history data (this search has already been made on TTT).
   * @param {object} state - State data
   * @param {string} mean - Transport mean
   * @param {number} increment - Additional number added to seed to induce variations between different transport means text blocks.
   * @returns {string|Fragment} - Block of text containing a header and a body for one transport mean.
   */
  getHistoryContent(state, mean, increment) {
    const seed = this.seed + increment;
    const meanData = state.tripData.transportInformation[mean];
    const origin = state.originCity.name;
    const destination = state.destinationCity.name;
    let content = '';

    const header = this.formatMeanHeader(origin, destination, mean);

    if (isEmpty(meanData)) {
      if (mean === MULTIMODAL) {
        return '';
      }
      content = <p>{this.formatNoData(origin, destination, mean)}</p>;
    } else {
      const companies = this.formatCompanies(meanData.bestItinerary.companies);
      const formattedTrips = this.getFormattedTrips(origin, destination);

      const textValues = {
        companies,
        intermediateStations: this.formatIntermediateStations(
          meanData.bestItinerary.stations.intermediateStations,
        ),
        companySentence: this.formatCompanySentence(
          companies,
          meanData.bestItinerary.companies.length,
          seed,
        ),
        itinerary: this.formatItineraryCities(
          origin,
          destination,
          meanData.bestItinerary.cities || [],
        ),
        formattedTrip1: formattedTrips[0],
        formattedTrip2: formattedTrips[1],
        minPrice: getRoundPrice(meanData.bestItinerary.price.min),
        maxPrice: getRoundPrice(meanData.bestItinerary.price.max),
        meanPrice: getRoundPrice(meanData.bestItinerary.price.mean),
        minDuration: formatDuration(meanData.bestItinerary.duration.min),
        maxDuration: formatDuration(meanData.bestItinerary.duration.max),
        meanDuration: formatDuration(
          Math.round(meanData.bestItinerary.duration.mean),
        ),
        firstDeparture: formatDuration(
          meanData.bestItinerary.departureTsOfFirst,
        ),
        lastDeparture: formatDuration(meanData.bestItinerary.departureTsOfLast),
        originStation: meanData.bestItinerary.stations.origin.name,
        destinationStation: meanData.bestItinerary.stations.destination.name,
        transportMean: this.intl.formatMessage(messages[mean]),
      };
      const chosenText = SPECIFIC_TEXTS[seed % SPECIFIC_TEXTS.length];
      content = (
        <p>
          <FormattedHTMLMessage {...messages[chosenText]} values={textValues} />
          {new Chance(this.chance.seed)
            .shuffle(SPECIFIC_TEXTS_TAIL[chosenText])
            .map((nextSentence) => (
              <Fragment key={nextSentence}>
                <FormattedHTMLMessage
                  {...messages[nextSentence]}
                  values={textValues}
                />
                &nbsp;
              </Fragment>
            ))}
          {this.formatDailyTrips(
            meanData.nbPerDay,
            formattedTrips[0],
            mean,
            seed,
          )}
        </p>
      );
    }

    return (
      <Fragment key={`historyContent${mean}`}>
        {header}
        <SubContent>{content}</SubContent>
      </Fragment>
    );
  }

  /**
   * @description Formats a block of text for one transport mean WITHOUT history data (this search has NOT already been made on TTT).
   * @param {object} state - State data
   * @param {string} mean - Transport mean
   * @param {number} increment - Additional number added to seed to induce variations between different transport means text blocks.
   * @returns {string|Fragment} - Block of text containing a header and a body for one transport mean.
   */
  getNoHistoryContent(state, mean, increment) {
    const seed = this.seed + increment;
    const meanData = state.tripData.transportInformation[mean];
    const origin = state.originCity.name;
    const destination = state.destinationCity.name;

    const header = this.formatMeanHeader(origin, destination, mean);
    let content = '';

    if (meanData && meanData.length) {
      const itineraries = meanData.slice(0, 3);

      content = (
        <Fragment>
          <p>{this.formatNoDataItineraryIntro(itineraries, mean, seed)}</p>
          <ul>
            {this.formatNoDataItinerariesList(
              state.tripData.cities.intermediateCities,
              itineraries,
              origin,
              destination,
              mean,
              seed,
            )}
          </ul>
        </Fragment>
      );
    } else {
      content = <p>{this.formatNoData(origin, destination, mean)}</p>;
    }

    return (
      <Fragment key={`noHistoryContent${mean}`}>
        {header}
        <SubContent>{content}</SubContent>
      </Fragment>
    );
  }

  /**
   * @description Formats the intro sentence for a transport mean without history data
   * @param {array} itineraries - Array of the first 3 possible itineraries for this mean
   * @param {string} mean - Transport mean
   * @param {number} seed - Seed incremented to induce variations between transport means
   * @returns {FormattedMessage} - Intro sentence
   */
  formatNoDataItineraryIntro(itineraries, mean, seed) {
    return (
      <FormattedMessage
        {...messages[
          SPECIFIC_TEXT_NO_DATA_INTRO[seed % SPECIFIC_TEXT_NO_DATA_INTRO.length]
        ]}
        values={{
          itinerariesNb: itineraries.length,
          itinerariesAvailable:
            itineraries.length > 1
              ? this.intl.formatMessage(messages.itinerariesAvailablePlural)
              : this.intl.formatMessage(messages.itinerariesAvailable),
          transportMean: this.intl.formatMessage(messages[mean]),
        }}
      />
    );
  }

  /**
   * @description Formats a list of possible itineraries for transport means with no history data
   * @param {{cityid: number, uniquename: string, localname: string stationIds: string[]}[]} intermediateCities - List of possible intermediate cities for all transport means
   * @param {{intermediateCities:number[], providers:*[], distanceAsCrowFlies:number, hasDirectTrip:boolean}[]} itineraries - Array of the first 3 possible itineraries for this mean
   * @param {string} origin - Name of the origin city
   * @param {string} destination - Name of the destination city
   * @param {string} mean - Transport mean
   * @param {number} seed - Seed incremented to  induce variations between transport means
   * @returns {Node[]} - Array of <li> for three possible itineraries
   */
  formatNoDataItinerariesList = (
    intermediateCities,
    itineraries,
    origin,
    destination,
    mean,
    seed,
  ) =>
    itineraries.map((itinerary, i) => {
      const formattedTrip = this.getFormattedTrip(origin, destination);
      const companies = this.formatCompanies(itinerary.providers);
      const companiesSentence = this.formatCompanySentence(
        companies,
        itinerary.providers.length,
        seed,
      );
      const itineraryStops = this.formatNoDataItineraryStops(
        origin,
        destination,
        intermediateCities,
        itinerary,
        seed,
      );
      const distanceSentence = this.formatNoDataItineraryDistance(
        Math.round(itinerary.distanceAsCrowFlies),
        seed,
      );

      return (
        <li>
          <p>
            <FormattedHTMLMessage
              {...messages[
                SPECIFIC_TEXT_NO_DATA_ITINERARIES[
                  (seed + i) % SPECIFIC_TEXT_NO_DATA_ITINERARIES.length
                ]
              ]}
              values={{
                origin,
                destination,
                companiesSentence,
                direct: itinerary.hasDirectTrip,
                stops: itineraryStops,
                formattedTrip,
                companies,
                transportMean: this.intl.formatMessage(messages[mean]),
              }}
            />
            {distanceSentence}
          </p>
        </li>
      );
    });

  /**
   * @description Formats a block of text for the carpooling
   * @param {object} state - State data
   * @param {string} mean - Transport mean
   * @param {number} increment - Additional number added to seed to induce variations between different transport means text blocks.
   * @returns {string|Fragment} - Block of text containing a header and a body.
   */
  getCarpoolingContent(state, mean, increment) {
    const meanData = state.tripData.transportInformation[mean];
    const origin = state.originCity.name;
    const destination = state.destinationCity.name;
    const header = this.formatMeanHeader(origin, destination, mean);
    let content = '';

    if (meanData && !isEmpty(meanData) && meanData.length === undefined) {
      content = (
        <FormattedHTMLMessage
          {...messages.specificTextCarpooling}
          values={{
            formattedTrip: this.getFormattedTrip(
              origin,
              destination,
              increment,
            ),
            companies: this.formatCompanies(meanData.bestItinerary.companies),
            minPrice: getRoundPrice(meanData.bestItinerary.price.min),
            maxPrice: getRoundPrice(meanData.bestItinerary.price.max),
            minDuration: formatDuration(meanData.bestItinerary.duration.min),
            maxDuration: formatDuration(meanData.bestItinerary.duration.max),
          }}
        />
      );
    } else {
      content = (
        <FormattedHTMLMessage
          {...messages.specificTextNoDataCarpooling}
          values={{
            formattedTrip: this.getFormattedTrip(
              origin,
              destination,
              increment,
            ),
          }}
        />
      );
    }

    return (
      <Fragment key="carpoolingContent">
        {header}
        <SubContent>{content}</SubContent>
      </Fragment>
    );
  }

  /**
   * @description Formats the header for the transport mean
   * @param {string} origin - Name of the origin city
   * @param {string} destination - Name of the destination city
   * @param {string} mean - Transport mean
   * @returns {Object} - Header h4
   */
  formatMeanHeader(origin, destination, mean) {
    return (
      <SpecificTextMeanHeader>
        <FormattedMessage
          {...messages[
            SPECIFIC_TEXT_HEADERS[this.seed % SPECIFIC_TEXT_HEADERS.length]
          ]}
          values={{
            origin,
            destination,
            transportMean: this.intl.formatMessage(messages[mean]),
            emoji: TRANSPORT_MEANS_EMOJIS[mean],
          }}
        />
      </SpecificTextMeanHeader>
    );
  }

  /**
   * @description Formats the content of a transport mean with no data (not even serviced on TTT)
   * @param {string} origin - Name of the origin city
   * @param {string} destination - Name of the destination city
   * @param {string} mean - Transport mean
   * @returns {FormattedHTMLMessage}
   */
  formatNoData(origin, destination, mean) {
    return (
      <FormattedHTMLMessage
        {...messages.specificTextNoData}
        values={{
          transportMean: this.intl.formatMessage(messages[mean]),
          formattedTrip: this.getFormattedTrip(origin, destination),
        }}
      />
    );
  }

  /**
   * @description Formats an array of companies into a stringified list
   * @param {{transportType: string, fullname: string}[]} companies - Companies involved in a trip (returned by api)
   * @returns {string} - List of companies separated with "and"
   */
  formatCompanies = (companies) =>
    companies
      .map((company) => company.fullname)
      .join(` ${this.intl.formatMessage(messages.and)} `);

  /**
   * @description Adds a subject and a verb to build a sentence around a list of companies, handling plurals
   * @param {string} formattedCompanies - Stringified list of companies
   * @param {number} companiesNb - Number of companies in the list
   * @param {number} seed - Seed incremented to induce variations between transport means
   * @returns {string} Complete sentence for companies
   */
  formatCompanySentence(formattedCompanies, companiesNb, seed) {
    let companyIntro = this.intl.formatMessage(messages.companyIntro);
    let companyVerb = this.intl.formatMessage(
      messages[COMPANY_VERBS[seed % COMPANY_VERBS.length]],
    );

    if (companiesNb > 1) {
      companyIntro = this.intl.formatMessage(messages.companyIntroPlural);
      companyVerb = this.intl.formatMessage(
        messages[COMPANY_VERBS_PLURAL[seed % COMPANY_VERBS_PLURAL.length]],
      );
    }

    return ` ${companyIntro} ${formattedCompanies} ${companyVerb}`;
  }

  /**
   * @description Formats the itinerary sentence, with the list of intermediate cities or the direct trip
   * @param {string} origin - Name of the origin city
   * @param {string} destination - Name of the destination city
   * @param {string[]} itineraryCities - Intermediate cities for this itinerary (returned by API) containing city fullname (ex: "Paris, Ile-de-France, France")
   * @returns {string} Sentence with the direct trip or the intermediate cities for the transport mean
   */
  formatItineraryCities(origin, destination, itineraryCities) {
    if (itineraryCities.length > 1) {
      const intermediateCities = this.formatEnumeratingSentence(
        itineraryCities.map((city) => city.split(',')[0]),
        ',',
      );

      return this.intl.formatMessage(messages.specificTextItinerary, {
        intermediateCities,
        origin,
        destination,
      });
    }

    return this.intl.formatMessage(messages.specificTextItineraryDirect, {
      origin,
      destination,
    });
  }

  /**
   * @description Formats the array of stations into a stringified list
   * @param {{id: number, name: string, latitude: number, longitude: number}[]} stations - Intermediate stations (Returned by API)
   * @returns {string} Sentence containing intermediate stations (if any) for the transport mean
   */
  formatIntermediateStations(stations) {
    if (stations.length) {
      const formattedStations = this.formatEnumeratingSentence(
        stations.map((station) => station.name),
        ', ',
      );

      return this.intl.formatMessage(messages.specificTextStations, {
        stations: formattedStations,
        stationsTranslated:
          stations.length > 1
            ? this.intl.formatMessage(messages.stations)
            : this.intl.formatMessage(messages.station),
      });
    }

    return '';
  }

  /**
   * @description Formats a sentence to display th number of daily trips for the transport mean
   * @param {number} dailyTrips - Number of daily trips for the mean
   * @param {string} formattedTrip - One variation of displaying the trip
   * @param {string} mean - Transport mean
   * @param {number} seed - Seed incremented to induce variations between transport means
   * @returns {FormattedHTMLMessage} Sentence displaying the number of daily trips
   */
  formatDailyTrips(dailyTrips, formattedTrip, mean, seed) {
    return (
      <FormattedHTMLMessage
        {...messages[
          SPECIFIC_TEXT_DAILY_TRIPS[seed % SPECIFIC_TEXT_DAILY_TRIPS.length]
        ]}
        values={{
          dailyTrips,
          transportMean: this.intl.formatMessage(
            messages[TRANSPORT_MEANS_PLURAL[mean]],
          ),
          formattedTrip,
        }}
      />
    );
  }

  /**
   * @description Formats the intermediate stops for a transport mean with no history data
   * @param {string} origin - Name of the origin city
   * @param {string} destination - Name of the destination city
   * @param {{cityid: number, uniquename: string, localname: string stationIds: string[]}[]} intermediateCities - List of possible intermediate cities for all transport means
   * @param {{intermediateCities:number[], providers:*[], distanceAsCrowFlies:number, hasDirectTrip:boolean}} itinerary - One possible itinerary
   * @param {number} seed - Seed incremented to induce variations between transport means
   * @returns {string} Sentence containing intermediate stops
   */
  formatNoDataItineraryStops = (
    origin,
    destination,
    intermediateCities,
    itinerary,
    seed,
  ) => {
    const stopsArray = itinerary.intermediateCities.reduce((acc, cityId) => {
      intermediateCities.forEach((city) => {
        if (city.cityId === cityId) {
          acc.push(city.localname.split(',')[0]);
        }
      });

      return acc;
    }, []);

    return this.intl.formatMessage(
      messages[
        SPECIFIC_TEXT_NO_DATA_ITINERARY_STOPS[
          seed % SPECIFIC_TEXT_NO_DATA_ITINERARY_STOPS.length
        ]
      ],
      { stops: this.formatEnumeratingSentence(stopsArray, ', ') },
    );
  };

  /**
   * @description Formats the distance of one possible itinerary
   * @param {number} distance - Distance in km rounded
   * @param {number} seed - Seed incremented to induce variations between transport means
   * @returns {FormattedHTMLMessage} Sentence to display the distance of one itinerary
   */
  formatNoDataItineraryDistance = (distance, seed) => (
    <FormattedHTMLMessage
      {...messages[
        SPECIFIC_TEXT_NO_DATA_ITINERARY_DISTANCE[
          seed % SPECIFIC_TEXT_NO_DATA_ITINERARY_DISTANCE.length
        ]
      ]}
      values={{ distance }}
    />
  );
}

export { SpecificTextGen };
